import { useQuery } from "@tanstack/react-query";
import { Building2, Plus, Sparkles, Wallet } from "lucide-react";
import { api, type CondominioListItem } from "../lib/api";
import { usePermissoes } from "../lib/permissoes";

// Acentos dos cards (vidro escuro com brilho neon no topo), alternados.
const CARD_ACCENTS: { hex: string; rgb: string }[] = [
  { hex: "#22C9B8", rgb: "34,201,184" },   // teal
  { hex: "#9684F6", rgb: "150,132,246" },  // violeta
  { hex: "#E8A13A", rgb: "232,161,58" },   // âmbar
  { hex: "#5A8CE6", rgb: "90,140,230" },   // azul
  { hex: "#34C484", rgb: "52,196,132" },   // verde
  { hex: "#E87BA4", rgb: "232,123,164" },  // rosa
];

interface Props {
  onAbrir: (id: number) => void;
  onNovo: () => void;
  onAbrirUso?: () => void;
  onAbrirAssistente?: () => void;
}

export function HomePage({ onAbrir, onNovo, onAbrirUso, onAbrirAssistente }: Props) {
  // Cadastrar um novo condomínio é ação de administrador (Dono).
  const { isDono, podeLerAlgum } = usePermissoes();
  // O Assistente (busca/IA) só aparece para quem pode usá-lo em algum condomínio.
  const podeAssistente = podeLerAlgum("assistente");
  const { data, isLoading, error } = useQuery<CondominioListItem[]>({
    queryKey: ["condominios"],
    queryFn: () => api.get<CondominioListItem[]>("/condominios"),
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-semibold text-slate-900">Seus condomínios</h2>
          <p className="text-sm text-slate-500 mt-1">
            Selecione um condomínio para ver balancetes e auditoria.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {onAbrirAssistente && podeAssistente && (
            <button
              type="button"
              onClick={onAbrirAssistente}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-300 hover:border-brand-400 hover:text-brand-700 text-slate-700 rounded-md text-sm font-medium transition"
              title="Buscar nas páginas e perguntar sobre os balancetes"
            >
              <Sparkles size={16} />
              Assistente
            </button>
          )}
          {onAbrirUso && (
            <button
              type="button"
              onClick={onAbrirUso}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-300 hover:border-brand-400 hover:text-brand-700 text-slate-700 rounded-md text-sm font-medium transition"
              title="Uso e custo do OCR por condomínio"
            >
              <Wallet size={16} />
              Uso &amp; Custos
            </button>
          )}
          {isDono && (
            <button
              type="button"
              onClick={onNovo}
              className="inline-flex items-center gap-2 px-4 py-2 bg-brand-700 hover:bg-brand-800 text-white rounded-md text-sm font-medium transition"
            >
              <Plus size={16} />
              Novo condomínio
            </button>
          )}
        </div>
      </div>

      {isLoading && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-32 bg-white border border-slate-200 rounded-card animate-pulse" />
          ))}
        </div>
      )}

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-md text-red-800">
          Erro ao carregar condomínios. O backend está rodando em 127.0.0.1:7423?
        </div>
      )}

      {data && data.length === 0 && (
        <EmptyState onNovo={onNovo} podeCadastrar={isDono} />
      )}

      {data && data.length > 0 && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.map((c, i) => {
            const acc = CARD_ACCENTS[i % CARD_ACCENTS.length];
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => onAbrir(c.id)}
                style={{
                  background:
                    `radial-gradient(130% 85% at 50% -12%, rgba(${acc.rgb},0.28), transparent 55%),` +
                    `linear-gradient(180deg,#17263f 0%,#0f1a2e 100%)`,
                  borderColor: "rgba(255,255,255,0.10)",
                  boxShadow: "0 14px 34px rgba(8,15,30,0.35), inset 0 1px 0 rgba(255,255,255,0.06)",
                }}
                className="group relative text-left p-4 border rounded-2xl transition-all hover:-translate-y-0.5"
              >
                <span
                  aria-hidden
                  className="absolute inset-x-0 top-0 h-[3px] rounded-t-2xl transition-all group-hover:h-[4px]"
                  style={{ background: acc.hex, boxShadow: `0 0 8px 1px rgba(${acc.rgb},0.9), 0 0 18px 3px rgba(${acc.rgb},0.55)` }}
                />
                <div className="flex items-start gap-3">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                    style={{ color: "#fff", background: `linear-gradient(150deg, rgba(${acc.rgb},0.95), rgba(${acc.rgb},0.5))` }}
                  >
                    <Building2 size={20} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-white truncate">{c.nome}</h3>
                    <p className="text-xs text-slate-300/70 mt-0.5">{c.numero_unidades} unidades</p>
                    {c.sindico_nome && (
                      <p className="text-xs text-slate-300/85 mt-2">Síndico: {c.sindico_nome}</p>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function EmptyState({ onNovo, podeCadastrar }: { onNovo: () => void; podeCadastrar: boolean }) {
  return (
    <div className="text-center py-16 bg-white border border-dashed border-slate-300 rounded-card">
      <Building2 className="mx-auto text-slate-300" size={48} />
      <h3 className="mt-4 text-lg font-medium text-slate-900">Nenhum condomínio ainda</h3>
      <p className="mt-1 text-sm text-slate-500">
        {podeCadastrar
          ? "Cadastre seu primeiro condomínio para começar a importar balancetes."
          : "Você ainda não tem acesso a nenhum condomínio. Peça ao administrador para liberar."}
      </p>
      {podeCadastrar && (
        <button
          type="button"
          onClick={onNovo}
          className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-brand-700 hover:bg-brand-800 text-white rounded-md text-sm font-medium"
        >
          <Plus size={16} />
          Cadastrar condomínio
        </button>
      )}
    </div>
  );
}
